import { Injectable } from '@angular/core';

/**
 * OpenPanel analytics (self-hosted, https://openpanel.buupass.com) — DEV-153.
 *
 * The tracker is loaded from src/index.html (script snippet); this service is
 * a typed wrapper around `window.op` for programmatic use. Auto-tracking
 * (screen views, outgoing links, `data-track` attributes, session replay) is
 * already on — you only need this service for:
 *
 *   - custom events:     this.openpanel.track('booking_completed', { route, amount })
 *   - identifying users: this.openpanel.identify({ profileId, email, firstName })
 *   - global props:      this.openpanel.setGlobalProperties({ operator: 'janamexpress' })
 *   - profile counters:  this.openpanel.increment('bookings')
 *   - logout:            this.openpanel.clear()
 *
 * Declarative tracking without code (preferred for simple clicks):
 *   <button data-track="select_seat" data-route="msa-nbo">...</button>
 */
declare global {
  interface Window {
    op?: (...args: unknown[]) => void;
  }
}

export interface OpenPanelProfile {
  profileId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  properties?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class OpenpanelService {
  private op(...args: unknown[]): void {
    if (typeof window !== 'undefined' && typeof window.op === 'function') {
      window.op(...args);
    }
  }

  /** Send a custom event with optional properties. */
  track(event: string, properties?: Record<string, unknown>): void {
    this.op('track', event, properties);
  }

  /** Attach the current visitor to a known user profile (e.g. after login). */
  identify(profile: OpenPanelProfile): void {
    this.op('identify', profile);
  }

  /**
   * Identify by the same phone-hash scheme the backend uses (itineraryapi's
   * util/openpanel.py, marketplaceapi's util/openpanel.py _profile_id): sha256
   * of normalized phone digits, truncated to 32 hex chars, prefixed "ph_".
   * MUST stay in sync with those Python implementations - same normalization,
   * same hash, same truncation - so a customer's web session and their
   * server-confirmed booking (fired from itineraryapi's ticketing endpoint)
   * attribute to the same OpenPanel profile. No-ops if the phone is empty.
   */
  async identifyByPhone(phone: string, extra?: Omit<OpenPanelProfile, 'profileId'>): Promise<void> {
    const profileId = await OpenpanelService.phoneProfileId(phone);
    if (!profileId) {
      return;
    }
    this.identify({ profileId, ...extra });
  }

  private static async phoneProfileId(phone: string): Promise<string | null> {
    let digits = String(phone || '').replace(/\D/g, '');
    if (!digits) {
      return null;
    }
    if (digits.length === 10 && digits.startsWith('0')) {
      digits = '254' + digits.slice(1);
    } else if (digits.length === 9 && /^[17]/.test(digits)) {
      digits = '254' + digits;
    }
    const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(digits));
    const hashHex = Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return 'ph_' + hashHex.slice(0, 32);
  }

  /** Properties attached to every subsequent event in this session. */
  setGlobalProperties(properties: Record<string, unknown>): void {
    this.op('setGlobalProperties', properties);
  }

  /** Increment a numeric property on the identified profile. */
  increment(property: string, value = 1): void {
    this.op('increment', { property, value });
  }

  /** Forget the current profile/session (call on logout). */
  clear(): void {
    this.op('clear');
  }
}
